import React, { useCallback } from 'react'
import { BoardItem, BoardItemImage } from 'models/BoardItem';
import { BoardItemPermission } from 'models/BoardItemPermission';
import { User } from 'models/User';
import { LinkIcon, LockIcon } from 'components/common/Icons';
import { usePopupDispatcher } from 'components/common/Popup';
import { Clickable } from 'components/common/Clickable';
import { TintButton } from 'components/common/Button';
import { SubsPopup } from 'components/common/BoardItemCard/SubsPopup';
import { transition } from 'actions/ExtensionAction';
import { Lang } from 'constants/Lang';

import styles from './styles.module.css';

type Props = {
    user: User
    item: BoardItem
    boardAuthorized: boolean
    isLogin: boolean
    onClickReload: () => void
}

export const BoardItemCard: React.FC<Props> = React.memo(({
    user,
    item,
    boardAuthorized,
    isLogin,
    onClickReload
}) => {
    const onClickLink = useCallback(() => {
        if (item.linkUrl) {
            transition(item.linkUrl)
        }
    }, [item.linkUrl])
    return (
        <div className={styles.root}>
            <BoardItemCardPublishedAt
                publishedAt={item.publishedAt}
            />
            <BoardItemCardContent
                user={user}
                item={item}
                visibleReloadButton={!boardAuthorized && isLogin}
                onClickReload={onClickReload}
                onClickLink={onClickLink} />
            <BoardItemCardNotice
                permission={item.permission} />
        </div>
    )
})

const BoardItemCardPublishedAt: React.FC<{
    publishedAt: Date
}> = ({
    publishedAt,
}) => {
    const formatted = new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    }).format(publishedAt);
    return (
        <div className={styles.publishedAt}>
            {formatted}
        </div>
     )
}

const BoardItemCardContent: React.FC<{
    user: User
    item: BoardItem
    visibleReloadButton: boolean
    onClickReload: () => void
    onClickLink: () => void
}> = ({
    user,
    item,
    visibleReloadButton,
    onClickReload,
    onClickLink
}) => {
    if (!item.hasPermission) {
        return (
            <LockedBoardItemCard
                subsUrl={user.subsUrl}
                visibleReloadButton={visibleReloadButton}
                onClickReloadButton={onClickReload} />
        )
    }

    const content = (
        <>
            { item.image?.url && <Image image={item.image} /> }
            <div>
                { item.title && <TitleRow title={item.title} /> }
                { item.description && <DescriptionRow description={item.description}/> }
                { item.domainUrl && <LinkRow domainUrl={item.domainUrl} /> }
            </div>
        </>
    )

    if (item.linkUrl) {
        return (
            <Clickable className={styles.card} type='group' onClick={onClickLink}>
                {content}
            </Clickable>
        )
    } else {
        return (
            <div className={styles.card}>
                {content}
            </div>
        )
    }
}

const LockedBoardItemCard: React.FC<{
    visibleReloadButton: boolean
    subsUrl: string
    onClickReloadButton: () => void
}> = ({
    visibleReloadButton,
    subsUrl,
    onClickReloadButton,
}) => {
    const popupDispatcher = usePopupDispatcher()
    const onClickRoot = useCallback(() => {
        const onConfirm = () => {
            popupDispatcher.close()
            transition(subsUrl)
        }
        popupDispatcher.show({
            children: <SubsPopup
                onConfirm={ onConfirm }
                onCancel={ popupDispatcher.close } />
        })
    }, [popupDispatcher, subsUrl])
    const content = (
        <>
            <div className={styles.lockedCardItem}>
                <LockIcon/>
                {Lang.ja.memberOnly}
            </div>
            { visibleReloadButton && <TintButton
                color='premium'
                onClick={onClickReloadButton}>
                {Lang.ja.subsReload}
            </TintButton> }
        </>
    )
    if (visibleReloadButton) {
        return (
            <div className={`${styles.card} ${styles.lockedCard}`}>
                {content}
            </div>
        )
    }
    return (
        <Clickable
            type='group'
            className={`${styles.card} ${styles.lockedCard}`}
            onClick={onClickRoot} >
            {content}
        </Clickable>
    )
}

const Image: React.FC<{ image: BoardItemImage }> = ({ image }) => (
    <img className={styles.image}
        src={image.url}
        width={image.width}
        height={image.height}
        alt={""}/>
)

const TitleRow: React.FC<{ title: string }> = ({title}) => {
    return (
        <div className={ `${styles.contentRow} ${styles.title}`}>
            { title }
        </div>
    )
}

const DescriptionRow: React.FC<{ description: string }> = ({description}) => (
    <p className={`${styles.contentRow} ${styles.description}`}>{description}</p>
)

const LinkRow: React.FC<{ domainUrl: string }> = ({domainUrl}) => (
    <div className={`${styles.contentRow} ${styles.link}`}>
        <LinkIcon />
        {domainUrl}
    </div>
)

const BoardItemCardNotice: React.FC<{
    permission: BoardItemPermission
}> = ({
    permission
}) => {
    if (permission.type === 'all') {
        return null
    }
    
    return (
        <div className={styles.notice} >
            <div className={styles.noticeTitle}>
                { permission.isAllSubsProducts ? Lang.ja.noticeMemberOnly : Lang.ja.noticeSubsProducts }
            </div>
            {
                permission.isAllSubsProducts || <div className={styles.noticeContent}>
                    {
                        permission.permittedSubsProducts.map((item) =>
                            <span className={styles.noticeContentItem} key={item.id}>
                                {item.name}
                            </span>
                        )
                    }
                </div>
            }
        </div>
    )
}
